import { mat4, vec3 } from 'gl-matrix';

import { Camera } from '../camera';
import { Context } from '../context/Context';
import { Mesh } from '../mesh';
import { Material } from '../material';
import { DrawableObject, RenderConfiguration, RenderMode, RenderSettings } from '../renderer';
import { BoundingBox, BoundingDimensions } from '../util';

/**
 * A bulk-instantiated cluster of identical, immutable mesh placements that
 * sits in the scene's `ObjectManager` octree as ONE entry. Replaces the per-
 * instance `RenderObject` model for scatter-style populations where:
 *
 *   1. Many placements share the same mesh + material.
 *   2. The placements are static (TRS never mutates after construction).
 *   3. They are loaded/evicted as a unit (typically per scatter tile).
 *
 * **Why this exists** — the per-instance `RenderObject` path turns into the
 * dominant CPU cost once scatter populations reach tens of thousands:
 *
 *   - Octree query iterates every visible instance (~38K visible at 42K total
 *     in the demo scene): 5+ ms/frame in `octreeQueryMs`.
 *   - `InstanceManager.prepareFrame` does an `objectToGroup` hash lookup per
 *     visible instance: 2+ ms.
 *   - `InstanceGroup.writeMatrices` re-copies every visible matrix every
 *     frame even though scatter is static: 5+ ms.
 *
 * Collapsing N instances into one octree entry replaces the per-instance
 * cost with per-cluster cost (N≈100..1000). With per-cluster LOD on top,
 * the matrix-copy phase becomes a direct `bufferSubData` from this group's
 * pre-baked Float32Array — no CPU-side staging of individual matrices.
 *
 * **Immutability contract** — once constructed, a RenderGroup's instance data
 * (`instanceMatrices`, `instanceSpheres`, `tileAABB`, `tileCenter`,
 * `instanceCount`) does NOT change. Tile streamers that need to update
 * placements destroy the group and build a new one. Anything that walks
 * `bounds` or expects `BoundingDimensions.bind(trs)` -style live updates is
 * out of bounds for a RenderGroup.
 */
export class RenderGroup implements DrawableObject {
  /**
   * Pre-baked world-space model matrices for every instance. Layout:
   * 16 floats per instance, contiguous, in the same order as `instanceSpheres`
   * and `instanceCount`. Uploaded directly to the GPU via `bufferSubData` —
   * never staged through a CPU-side scratch buffer.
   */
  public readonly instanceMatrices: Float32Array;

  /**
   * Pre-baked world-space bounding spheres, 4 floats per instance:
   * `(centerX, centerY, centerZ, radius)`. Used by per-instance shadow culling
   * if/when we re-add it; currently kept for completeness so the surface API
   * can answer "what instances are in this AABB" without reconstructing.
   */
  public readonly instanceSpheres: Float32Array;

  public readonly instanceCount: number;
  public readonly tileKey: string;
  public readonly mesh: Mesh;
  public readonly material: Material | undefined;

  /**
   * Tile-center world position. Used by `InstanceGroup` for per-cluster LOD
   * selection (one LOD per RenderGroup, distance computed once from this
   * point). Computed at construction as the centroid of `instanceSpheres`.
   */
  public readonly tileCenter: vec3;

  /**
   * Union AABB of every instance's bounding sphere. Drives octree placement
   * via the `BoundingDimensions` mirror exposed at `bounds`.
   */
  public readonly tileAABB: BoundingBox;

  public readonly bounds: BoundingDimensions;
  public renderConfiguration: RenderConfiguration;

  /**
   * Set by `InstanceManager.registerGroup` after this group joins an
   * InstanceGroup. Cleared by `unregisterGroup`. Never mutated by the
   * RenderGroup itself; exposed so `prepareFrame` can route the group to its
   * draw target without a Map lookup per visible group.
   */
  public targetInstanceGroup: import('../renderer').InstanceGroup | null = null;

  /**
   * Build a RenderGroup from N pre-baked instances. The matrices and spheres
   * arrays become owned by the RenderGroup — callers must hand over a
   * freshly-allocated buffer and may not retain or mutate it after
   * construction. `InstanceGroup.updateAndUpload` reads `instanceMatrices`
   * directly as a `bufferSubData` source, so any post-construction mutation
   * would land in the GPU buffer mid-frame.
   *
   * @param tileKey Identifies the source tile so streamers can ref-count and
   *   evict cleanly. Format and meaning are streamer-defined.
   * @param mesh The shared GL mesh that all instances draw with.
   * @param material The shared material (may be undefined for color-only
   *   meshes; matches the rest of the engine's instancing eligibility rules).
   * @param instanceMatrices `16 * count` floats, world-space mat4 per instance.
   * @param instanceSpheres `4 * count` floats, `(cx, cy, cz, r)` per instance.
   */
  constructor(
    tileKey: string,
    mesh: Mesh,
    material: Material | undefined,
    instanceMatrices: Float32Array,
    instanceSpheres: Float32Array,
  ) {
    if (instanceMatrices.length % 16 !== 0) {
      throw new Error(
        `RenderGroup matrices must be a multiple of 16 floats; got ${instanceMatrices.length}`,
      );
    }
    const count = instanceMatrices.length / 16;
    if (instanceSpheres.length !== count * 4) {
      throw new Error(
        `RenderGroup spheres length ${instanceSpheres.length} mismatches matrix count ${count} (expected ${count * 4})`,
      );
    }

    this.tileKey = tileKey;
    this.mesh = mesh;
    this.material = material;
    this.instanceMatrices = instanceMatrices;
    this.instanceSpheres = instanceSpheres;
    this.instanceCount = count;

    // Compute tile-center as the centroid of instance sphere centers, and
    // the union AABB as the union of every sphere's enclosing box. We use
    // spheres rather than per-instance OBBs because the streamer hands them
    // over already and the union is conservative either way.
    const min = vec3.fromValues(Infinity, Infinity, Infinity);
    const max = vec3.fromValues(-Infinity, -Infinity, -Infinity);
    let sumX = 0;
    let sumY = 0;
    let sumZ = 0;
    for (let i = 0; i < count; i++) {
      const o = i * 4;
      const cx = instanceSpheres[o];
      const cy = instanceSpheres[o + 1];
      const cz = instanceSpheres[o + 2];
      const r = instanceSpheres[o + 3];
      sumX += cx;
      sumY += cy;
      sumZ += cz;
      if (cx - r < min[0]) min[0] = cx - r;
      if (cy - r < min[1]) min[1] = cy - r;
      if (cz - r < min[2]) min[2] = cz - r;
      if (cx + r > max[0]) max[0] = cx + r;
      if (cy + r > max[1]) max[1] = cy + r;
      if (cz + r > max[2]) max[2] = cz + r;
    }
    if (count > 0) {
      this.tileCenter = vec3.fromValues(sumX / count, sumY / count, sumZ / count);
      this.tileAABB = new BoundingBox(min, max);
    } else {
      this.tileCenter = vec3.create();
      this.tileAABB = new BoundingBox([0, 0, 0], [0, 0, 0]);
    }

    // BoundingDimensions wrapper so ObjectManager's existing octree machinery
    // (OctreeEntry → boundingBox / boundingSphere) treats this group like any
    // other DrawableObject. Static for the lifetime of the RenderGroup.
    this.bounds = BoundingDimensions.createFromBox(this.tileAABB.min, this.tileAABB.max);

    this.renderConfiguration = {
      culling: 'octree',
      lightning: ['dynamic'],
      static: true,
      // GBuffer = picked up by the gbuffer cull; Depth = picked up by shadow
      // casters (both static-cache and per-frame). No Picking — scatter is
      // explicitly unpickable; the depth blit from the G-buffer still
      // occludes pickables behind them.
      modes: [RenderMode.GBuffer, RenderMode.Depth],
    };
  }

  /**
   * RenderGroup never renders directly — it's always proxied through its
   * `InstanceGroup` target inside `InstanceManager.prepareFrame` /
   * `GBufferPass` / `DepthMapRenderer`. This stub satisfies the
   * `DrawableObject` contract for any code path that accidentally falls
   * through to a per-object render() call.
   */
  public render(_ctx: Context, _camera: Camera, _settings: RenderSettings): void {
    // intentionally empty — see class doc.
  }

  /** No GL resources of our own; mesh and material are shared and freed elsewhere. */
  public destroy(): void {}

  /**
   * Convenience: extract one instance's mat4 into the supplied target.
   * Useful for tests and debug overlays. NOT a hot-path API.
   */
  public getInstanceMatrix(index: number, out: mat4): mat4 {
    if (index < 0 || index >= this.instanceCount) {
      throw new Error(`RenderGroup instance index ${index} out of range [0, ${this.instanceCount})`);
    }
    const off = index * 16;
    for (let i = 0; i < 16; i++) out[i] = this.instanceMatrices[off + i];
    return out;
  }
}
